import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Logo } from '@/shared/components/logo'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'

/**
 * The frame the pages that render without a session share.
 *
 * Deliberately not the application shell: none of these have a workspace, a
 * sidebar or a user to hang a menu off, and reusing the shell would mean every
 * part of it learning to cope with a null session.
 *
 * The brand / eyebrow / heading / body / action shape is the one the other
 * Lithos services serve their signed-out screens in, so somebody who meets two
 * of them in an afternoon meets one design.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <main className="app-background flex min-h-dvh items-center justify-center p-4">
      <div className="on-surface animate-fade-up bg-card flex w-full max-w-[26rem] flex-col gap-6 rounded-lg p-6">
        <Wordmark />
        {children}
      </div>
    </main>
  )
}

/**
 * What the page says, above whatever way forward it offers.
 *
 * `alert` tints the eyebrow as a refusal rather than a status -- a soft tint
 * either way, never a saturated fill.
 */
export function AuthHeading({
  eyebrow,
  heading,
  alert = false,
  children,
}: {
  eyebrow: string
  heading: string
  alert?: boolean
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      <Badge
        variant="ghost"
        className={cn(
          'mb-1 self-start',
          alert
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted-foreground/12 text-muted-foreground',
        )}
      >
        {eyebrow}
      </Badge>
      <h1 className="text-card-foreground text-lg leading-snug font-semibold">
        {heading}
      </h1>
      {children}
    </div>
  )
}

/** The same lockup the sidebar opens with, so the two surfaces agree. */
function Wordmark() {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-2.5">
      <Logo className="h-6 w-auto" />
      <span className="text-sm font-semibold tracking-tight">
        {t('brand.name')}
        <span className="ml-1.5 underline decoration-1 underline-offset-2">
          {t('brand.app')}
        </span>
      </span>
    </div>
  )
}
